// src/pages/WhatsappAds.jsx
//
// WhatsApp Ads Manager — the campaign list, the create wizard, and (inline) the
// full per-campaign manager: recipients, template parameters, schedule,
// pause/resume/run-now and run history.
//
// The per-campaign half lives in CampaignManager.jsx and is mounted here rather
// than duplicated, so there is one implementation of the controls.
//
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiBarChart2, FiSearch, FiRefreshCw, FiPlus, FiX,
  FiClock, FiUsers, FiSettings, FiTrendingUp,
} from "react-icons/fi";

import {
  CampaignDetail, Banner, Pill, DAYS, TIMEZONES, call, fmt,
} from "./CampaignManager";
import { useWabaAccounts, withAccount, accountLabel } from "../lib/waba";

const COUNTRIES = ["India", "UAE", "Egypt", "Kenya", "Srilanka"];
const CATEGORIES = ["MARKETING", "UTILITY", "AUTHENTICATION", "SERVICE"];

const EMPTY_FORM = {
  campaign_name: "", template_name: "", template_category: "",
  campaign_budget: "", country: "India", language_code: "en_US",
  schedule_type: "once", days_of_week: [], send_time: "10:00",
  timezone: "Asia/Kolkata", trigger_mode: "scheduled",
  start_date: "", end_date: "",
};

/* -------------------------------------------------------------------------
   Cards
------------------------------------------------------------------------- */
function SkeletonCard() {
  return (
    <div className="h-full animate-pulse rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start justify-between">
        <div className="h-5 w-2/3 rounded bg-gray-200" />
        <div className="h-6 w-20 rounded-full bg-gray-200" />
      </div>
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div className="h-4 w-28 rounded bg-gray-200" />
        <div className="h-4 w-24 rounded bg-gray-200" />
      </div>
      <div className="h-4 w-full rounded bg-gray-200" />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="truncate text-sm font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function CampaignCard({ item, onManage, onStats }) {
  return (
    <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-all hover:border-gray-300 hover:shadow-md">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-gray-900 sm:text-lg" title={item.campaign_name}>
            {item.campaign_name || "Untitled"}
          </h3>
          <div className="mt-1 truncate font-mono text-xs text-gray-500">
            {item.template_name || "no template"}
          </div>
          {/* Which number this campaign goes out from. On a platform with one
              WABA this was implicit; with several, a card that does not say it
              is a card you cannot act on. */}
          {item.waba_account && (
            <div className="mt-1 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              {item.waba_account}
            </div>
          )}
        </div>
        <Pill status={item.status} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Stat label="Recipients" value={item.recipients ?? 0} />
        <Stat label="Runs" value={item.run_count ?? 0} />
        <Stat label="Budget" value={`₹${Number(item.campaign_budget || 0).toFixed(2)}`} />
        <Stat label="Country" value={item.country || "—"} />
      </div>

      <div className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-sm">
        <div className="flex items-start gap-2 text-gray-700">
          <FiClock className="mt-0.5 shrink-0 text-gray-500" />
          <span className="min-w-0 break-words">
            {item.start_date ? item.schedule?.description || "—" : "Not scheduled yet"}
          </span>
        </div>
        <div className="mt-1 text-xs text-gray-500">
          Next run: {item.status === "PAUSED" ? "paused" : fmt(item.next_run_at)}
        </div>
      </div>

      <div className="mt-4 flex gap-2 pt-1">
        <button onClick={() => onManage(item)}
          className="flex min-h-[40px] flex-1 items-center justify-center gap-2 rounded-lg bg-[#F96168] px-3 text-sm font-semibold text-black hover:bg-[#EA434B]">
          <FiSettings /> Manage
        </button>
        <button onClick={() => onStats(item)}
          className="flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-gray-300 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50">
          <FiTrendingUp /> Stats
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Create wizard

   The old dialog created a campaign with no schedule and then asked for an
   XLSX in the same modal, which left half-built campaigns behind whenever the
   upload failed. Now creation sets name + template + schedule in one commit,
   and recipients are uploaded in the manager, where the parameter and warning
   feedback lives.
------------------------------------------------------------------------- */
function CreateCampaignModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  // Which WhatsApp Business Account the campaign will send from. It also
  // decides which templates are on offer below — a template only exists on the
  // account it was approved on, so offering the whole platform's templates
  // here would let an operator pick one this account cannot send.
  const {
    accounts: wabaAccounts,
    selected: wabaAccount,
    setSelected: setWabaAccount,
    loading: wabaLoading,
    error: wabaError,
  } = useWabaAccounts();
  const [templateNames, setTemplateNames] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onChange = (e) => set(e.target.name, e.target.value);

  useEffect(() => {
    if (!open) return undefined;
    setForm(EMPTY_FORM);
    setErr("");
    setTemplateInfo(null);
    return undefined;
  }, [open]);

  // Reloaded when the account changes, and the previously chosen template is
  // cleared: a name that exists on one account usually does not exist on
  // another, and silently keeping it would create a campaign that fails every
  // recipient at its first run.
  useEffect(() => {
    if (!open || !wabaAccount) return undefined;
    let cancelled = false;
    setForm((f) => (f.template_name ? { ...f, template_name: "" } : f));
    setTemplateInfo(null);
    (async () => {
      setTemplatesLoading(true);
      try {
        const data = await call(withAccount("/templates/names/approved", wabaAccount));
        if (!cancelled) setTemplateNames(Array.isArray(data?.names) ? data.names : []);
      } catch (e) {
        if (!cancelled) setErr(`Could not load templates: ${e.message}`);
      } finally {
        if (!cancelled) setTemplatesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, wabaAccount]);

  // Pull the template's language and category so the operator does not have to
  // retype values Meta already knows, and can see how many {{n}} it expects.
  useEffect(() => {
    if (!open || !form.template_name) { setTemplateInfo(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const data = await call(withAccount(
          `/templates/${encodeURIComponent(form.template_name)}?status=APPROVED`, wabaAccount));
        const tpl = data?.template;
        if (cancelled || !tpl) return;
        const body = (tpl.components || []).find((c) => c?.type === "BODY")?.text || "";
        const indexes = [...body.matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((m) => Number(m[1]));
        setTemplateInfo({
          body,
          category: tpl.category || "",
          language: tpl.language || "en_US",
          paramCount: indexes.length ? Math.max(...indexes) : 0,
        });
        setForm((f) => ({
          ...f,
          language_code: tpl.language || f.language_code,
          template_category: tpl.category || f.template_category,
        }));
      } catch {
        if (!cancelled) setTemplateInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open, form.template_name, wabaAccount]);

  const problem = useMemo(() => {
    if (!form.campaign_name.trim()) return "Give the campaign a name.";
    if (!wabaAccount) return "Select the WhatsApp account to send from.";
    if (!form.template_name) return "Select a template.";
    if (!Number(form.campaign_budget)) return "Set a budget above zero.";
    if (!form.start_date) return "Pick the date this campaign starts.";
    if (form.schedule_type === "weekly" && !form.days_of_week.length)
      return "Pick at least one day of the week.";
    if (form.schedule_type !== "once" && !form.end_date)
      return "A recurring campaign needs an end date.";
    if (form.end_date && new Date(form.end_date) < new Date(form.start_date))
      return "The end date is before the start date.";
    return null;
  }, [form, wabaAccount]);

  const submit = async () => {
    if (problem) { setErr(problem); return; }
    setLoading(true);
    setErr("");
    try {
      const out = await call("/campaigns/create", {
        method: "POST",
        body: JSON.stringify({
          campaign_name: form.campaign_name.trim(),
          start_date: new Date(form.start_date).toISOString(),
          // A one-shot has no window; reuse the start so the column is never
          // null, which is what the ENDED date fallbacks key off.
          end_date: new Date(form.end_date || form.start_date).toISOString(),
          campaign_budget: form.campaign_budget,
          template_name: form.template_name,
          template_category: form.template_category || null,
          country: form.country,
          waba_account: wabaAccount,
          language_code: form.language_code,
          schedule_type: form.schedule_type,
          days_of_week: form.schedule_type === "weekly" ? form.days_of_week : [],
          send_time: form.send_time,
          timezone: form.timezone,
          trigger_mode: form.trigger_mode,
        }),
      });
      onCreated(out.campaign_id);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const toggleDay = (n) =>
    set("days_of_week", form.days_of_week.includes(n)
      ? form.days_of_week.filter((x) => x !== n)
      : [...form.days_of_week, n].sort((a, b) => a - b));

  const field = "w-full rounded-lg border border-gray-300 px-3 py-2.5";
  const label = "mb-1 block text-sm font-semibold text-gray-700";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 p-4">
          <h3 className="text-lg font-semibold text-gray-900">New WhatsApp Campaign</h3>
          <button onClick={onClose} className="rounded-lg p-2 hover:bg-gray-50">
            <FiX className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          {/* Ahead of the template on purpose: it is what decides which
              templates the next field can even offer. */}
          <div>
            <label className={label}>WhatsApp account</label>
            <select
              value={wabaAccount}
              disabled={wabaLoading || !wabaAccounts.length}
              onChange={(e) => setWabaAccount(e.target.value)}
              className={`${field} bg-white`}
            >
              {wabaLoading && <option value="">Loading accounts…</option>}
              {!wabaLoading && !wabaAccounts.length && (
                <option value="">No accounts configured</option>
              )}
              {!wabaLoading && wabaAccounts.map((a) => (
                <option key={a.name} value={a.name}>{accountLabel(a)}</option>
              ))}
            </select>
            <div className="mt-1 text-xs text-gray-500">
              {wabaError
                ? <span className="text-red-600">{wabaError}</span>
                : !wabaLoading && !wabaAccounts.length
                  ? "Add one under Settings → WhatsApp Business Accounts."
                  : "Every message in this campaign is sent from this WhatsApp number."}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className={label}>Campaign name</label>
              <input name="campaign_name" value={form.campaign_name} onChange={onChange} className={field} />
            </div>
            <div>
              <label className={label}>Template</label>
              <select name="template_name" value={form.template_name} onChange={onChange}
                      className={`${field} bg-white`} disabled={templatesLoading}>
                <option value="">
                  {templatesLoading
                    ? "Loading templates…"
                    : templateNames.length
                      ? "Select a template"
                      : "No approved templates on this account"}
                </option>
                {templateNames.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Category</label>
              <select name="template_category" value={form.template_category} onChange={onChange}
                      className={`${field} bg-white`}>
                <option value="">Not set</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Language</label>
              <input name="language_code" value={form.language_code} onChange={onChange} className={field} />
            </div>
            <div>
              <label className={label}>Budget (₹)</label>
              <input type="number" step="0.01" name="campaign_budget"
                     value={form.campaign_budget} onChange={onChange} className={field} />
            </div>
            <div>
              <label className={label}>Country</label>
              <select name="country" value={form.country} onChange={onChange} className={`${field} bg-white`}>
                {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {templateInfo && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm">
              <div className="text-xs uppercase text-gray-500">Template body</div>
              <div className="mt-1 whitespace-pre-wrap text-gray-800">{templateInfo.body || "—"}</div>
              <div className="mt-2 text-xs text-gray-600">
                {templateInfo.paramCount
                  ? `Needs ${templateInfo.paramCount} parameter(s). Add Param 1…Param ${templateInfo.paramCount} columns to your file, or set defaults after creating.`
                  : "No variables to fill in."}
              </div>
            </div>
          )}

          <div>
            <div className={label}>Repeat</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "once", l: "One-shot", d: "Send once" },
                { v: "daily", l: "Daily", d: "Every day" },
                { v: "weekly", l: "Weekly", d: "Chosen days" },
              ].map((o) => (
                <button key={o.v} type="button" onClick={() => set("schedule_type", o.v)}
                  className={`rounded-xl border px-3 py-3 text-left transition ${
                    form.schedule_type === o.v
                      ? "border-[#F96168] bg-[#F96168]/10 ring-1 ring-[#F96168]"
                      : "border-gray-200 hover:bg-gray-50"}`}>
                  <div className="text-sm font-semibold text-gray-900">{o.l}</div>
                  <div className="text-xs text-gray-500">{o.d}</div>
                </button>
              ))}
            </div>
          </div>

          {form.schedule_type === "weekly" && (
            <div>
              <div className={label}>Days</div>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d) => (
                  <button key={d.n} type="button" onClick={() => toggleDay(d.n)}
                    className={`min-h-[44px] min-w-[52px] rounded-xl border px-3 text-sm font-semibold transition ${
                      form.days_of_week.includes(d.n)
                        ? "border-[#F96168] bg-[#F96168] text-white"
                        : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                    {d.s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className={label}>
                {form.schedule_type === "once" ? "Send on" : "Starts"}
              </label>
              <input type="datetime-local" name="start_date" value={form.start_date}
                     onChange={onChange} className={field} />
            </div>
            {form.schedule_type !== "once" && (
              <>
                <div>
                  <label className={label}>Ends</label>
                  <input type="datetime-local" name="end_date" value={form.end_date}
                         onChange={onChange} className={field} />
                </div>
                <div>
                  <label className={label}>Send at</label>
                  <input type="time" name="send_time" value={form.send_time}
                         onChange={onChange} className={field} />
                </div>
              </>
            )}
            <div>
              <label className={label}>Timezone</label>
              <select name="timezone" value={form.timezone} onChange={onChange}
                      className={`${field} bg-white`}>
                {TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className={label}>When recipients are uploaded</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "scheduled", l: "Wait for schedule", d: "Send at the next slot" },
                { v: "immediate", l: "Send immediately", d: "Fire as soon as the list lands" },
              ].map((o) => (
                <button key={o.v} type="button" onClick={() => set("trigger_mode", o.v)}
                  className={`rounded-xl border px-3 py-3 text-left transition ${
                    form.trigger_mode === o.v
                      ? "border-[#F96168] bg-[#F96168]/10 ring-1 ring-[#F96168]"
                      : "border-gray-200 hover:bg-gray-50"}`}>
                  <div className="text-sm font-semibold text-gray-900">{o.l}</div>
                  <div className="text-xs text-gray-500">{o.d}</div>
                </button>
              ))}
            </div>
          </div>

          {(err || problem) && <Banner kind={err ? "error" : "warn"}>{err || problem}</Banner>}
        </div>

        <div className="border-t border-gray-100 p-4">
          <button onClick={submit} disabled={loading || !!problem}
            className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-[#F96168] px-4 font-semibold text-black disabled:opacity-50">
            <FiPlus /> {loading ? "Creating…" : "Create and add recipients"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Page
------------------------------------------------------------------------- */
export default function WhatsAppAds() {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [countryFilter, setCountryFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [openCreate, setOpenCreate] = useState(false);
  const [managedId, setManagedId] = useState(null);

  // The grid shows one account's campaigns: the account chosen in the header,
  // which is also the one the create dialog defaults to.
  const { selected: listAccount } = useWabaAccounts();

  const fetchCampaigns = useCallback(async () => {
    if (!listAccount) return;
    setLoading(true);
    setErr("");
    try {
      const data = await call(withAccount("/whatsapp-campaigns/list?limit=200", listAccount));
      setItems(Array.isArray(data?.campaigns) ? data.campaigns : []);
    } catch (e) {
      setErr(e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [listAccount]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const countries = useMemo(() => {
    const found = items.map((i) => (i.country || "").trim()).filter(Boolean);
    return Array.from(new Set([...found, ...COUNTRIES])).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((c) => {
      const matchesQuery = !q
        || c.campaign_name?.toLowerCase().includes(q)
        || c.template_name?.toLowerCase().includes(q)
        || String(c.campaign_id || "").toLowerCase().includes(q);
      const matchesCountry = countryFilter === "ALL"
        || (c.country || "").toLowerCase() === countryFilter.toLowerCase();
      const matchesStatus = statusFilter === "ALL" || c.status === statusFilter;
      return matchesQuery && matchesCountry && matchesStatus;
    });
  }, [items, query, countryFilter, statusFilter]);

  if (managedId) {
    return (
      <div className="page mx-auto max-w-5xl">
        <CampaignDetail
          campaignId={managedId}
          onBack={() => { setManagedId(null); fetchCampaigns(); }}
          onChanged={fetchCampaigns}
        />
      </div>
    );
  }

  return (
    <div className="page mx-auto" style={{ maxWidth: "1800px" }}>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">WhatsApp Ads Manager</h1>
          <p className="text-sm text-gray-600">
            Create campaigns, upload recipients, schedule them, and control every run.
          </p>
        </div>
        <button onClick={() => setOpenCreate(true)}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-green-600 px-4 font-semibold text-white hover:bg-green-700">
          <FiPlus /> Create campaign
        </button>
      </div>

      <div className="mb-8 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="relative w-full flex-1">
            <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, template or ID…"
              className="min-h-[44px] w-full rounded-lg border border-gray-300 pl-9 pr-3 focus:outline-none focus:ring-2 focus:ring-[#F96168]" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-3 lg:w-48">
            <option value="ALL">All statuses</option>
            {["DRAFT", "ACTIVE", "PAUSED", "COMPLETED", "ENDED"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}
            className="min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-3 lg:w-48">
            <option value="ALL">All countries</option>
            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={fetchCampaigns}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-[#F96168] px-4 font-semibold text-black hover:bg-[#EA434B]">
            <FiRefreshCw /> Refresh
          </button>
        </div>

        {err && (
          <div className="mt-4">
            <Banner kind="error" onClose={() => setErr("")}>{err}</Banner>
          </div>
        )}
      </div>

      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          Campaigns <span className="text-gray-500">({filtered.length})</span>
        </h2>
        <span className="hidden items-center gap-1 text-xs text-gray-500 sm:flex">
          <FiUsers /> {items.reduce((n, c) => n + (c.recipients || 0), 0)} recipients across all campaigns
        </span>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-10 text-center shadow-sm">
          <FiBarChart2 className="mx-auto mb-3 h-12 w-12 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">No campaigns found</h3>
          <p className="mt-1 text-gray-600">
            {items.length ? "Try adjusting the search or filters." : "Create your first campaign to get started."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((c) => (
            <CampaignCard
              key={c.campaign_id}
              item={c}
              onManage={(x) => setManagedId(x.campaign_id)}
              onStats={(x) => navigate(`/whatsapp-ads/summary?campaign_id=${x.campaign_id}`,
                                       { state: { campaign: x } })}
            />
          ))}
        </div>
      )}

      <CreateCampaignModal
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={(id) => {
          setOpenCreate(false);
          fetchCampaigns();
          // Straight into the manager: a campaign with no recipients cannot do
          // anything yet, and that is the next thing the operator needs.
          setManagedId(id);
        }}
      />
    </div>
  );
}
